import { Category } from '@commerce-product-webcomponents/sdk';
import { PreferenceService, StorageService } from '@nextgen-web-framework/core';
import { BucketField } from '@commerce-search-webcomponents/sdk';
import {
  CachedCategoryAndSolutionData,
  CategoryAndSolutionDetails,
  CategoryTree,
  FindCatOrSolFacetDetailsMethodProps,
  Hierarchy,
} from './types/propTypes';

const ROOT_CAT_ID = 'root';
export const CAT_AND_SOL_STORAGE_KEY = 'categoryAndSolution';

export const fetchCategoriesAndSolutions = (
  saveCategoryAndSolution: (data?: CategoryAndSolutionDetails) => void,
  fetchCategoryOrSolutionData: (field: BucketField.CATEGORY | BucketField.SOLUTION) => void,
  isPLP?: boolean,
) => {
  // Check in cache first, if not found, get it from API
  const { locale } = PreferenceService.getPreferences();
  const data = (StorageService.getSessionStorage()?.retrieve(CAT_AND_SOL_STORAGE_KEY) ||
    {}) as CachedCategoryAndSolutionData;
  const { category, solution } = data[locale] ?? {};
  saveCategoryAndSolution({
    category: buildCategoryHierarchy(category, isPLP),
    solution: buildCategoryHierarchy(solution, isPLP),
  });
  if (!category) {
    fetchCategoryOrSolutionData(BucketField.CATEGORY);
  }
  if (!solution) {
    fetchCategoryOrSolutionData(BucketField.SOLUTION);
  }
};

export const findCategoryOrSolutionFacetDetails = ({
  bucketField,
  facets,
  isPLP,
  masterCategoryAndSolutionData,
  searchedKeyword,
}: FindCatOrSolFacetDetailsMethodProps) => {
  const bucket = facets.find(({ field }) => field === bucketField);
  const list = masterCategoryAndSolutionData?.[bucketField];
  if (!bucket?.values || !list) {
    return undefined;
  }
  let plpList: CategoryTree;
  if (list[searchedKeyword]?.parent == ROOT_CAT_ID) {
    plpList = { [searchedKeyword]: list[searchedKeyword], ...list[searchedKeyword].children };
  } else {
    const parentId = list[searchedKeyword]?.parent;
    plpList = parentId ? { [parentId]: list[parentId], ...list[parentId].children } : {};
  }
  const ids = bucket.values.map(({ name }) => name).filter((name) => name && (!isPLP || plpList[name])) as string[];
  const data = ids.reduce((result: CategoryTree, id) => {
    const bucketDetail = list?.[id];
    return { ...result, ...(bucketDetail && { [id]: bucketDetail }) };
  }, {});

  return data;
};

export const buildCategoryHierarchy = (categories: Category[] = [], isPLP?: boolean) => {
  const root: Hierarchy = { name: ROOT_CAT_ID, attribs: {}, children: {} };
  categories.forEach((category) => {
    const parts = category.path.split('/').filter(Boolean);
    let currentLevel = root;
    parts.forEach((part, index) => {
      // check currentLevel should not undefined it, it happens when duplicate category/solutions steup in BE
      if (currentLevel) {
        const node = {
          name: part,
          attribs: {},
          children: {},
          parent: currentLevel.name,
        };
        if (!root.children[part]) {
          root.children[part] = node;
        }
        if (isPLP && !currentLevel.children[part]) {
          currentLevel.children[part] = node;
        }
        if (index === parts.length - 1) {
          root.children[part].attribs = category.attribs;
        }
        currentLevel = currentLevel.children[part];
      }
    });
  });

  return root.children;
};
